'use client';

import { useMemo } from 'react';
import { Sparkles } from 'lucide-react';
import {
  gatingHintsFor,
  savedScopeOf,
  scopeOf,
  suggestSafeDefaultPolicy,
  suggestSafeDefaultScope,
  type ToolGatingHints,
  type ToolPolicy,
  type ToolPolicyConfig,
  type ToolPolicyValue,
  type ToolScope,
} from '@/lib/agents/tool-policies';
import { useT } from '@/components/i18n-provider';

export interface ToolPolicyItem {
  name: string;
  description?: string;
  destructive?: boolean;
  /**
   * SDK decorator-derived hints. When omitted the editor falls back to
   * the static {@link gatingHintsFor} registry keyed by tool name, so
   * existing callers that don't supply this field still get the right
   * "Set safe defaults" treatment for known SDK tools.
   */
  gating?: ToolGatingHints;
}

interface Props {
  available: ToolPolicyItem[];
  policies: Record<string, ToolPolicyValue>;
  onChange: (next: Record<string, ToolPolicyValue>) => void;
  /**
   * The scope a row with no SAVED scope actually runs with, shown for it
   * (S-457, 2026-10-09). An OpenAPI operation on an owned agent is
   * owner-only until an owner opens it; an MCP tool stays open until one
   * closes it. Omitted (messaging integrations): the runtime reads such a
   * row as `suggestSafeDefaultScope`, and so does the editor.
   */
  defaultScope?: ToolScope;
  /** Optional label rendered above the per-tool list. */
  label?: string;
  /** When true, the legacy "All" bulk action is hidden. */
  hideBulk?: boolean;
  /** Fill the parent height; useful inside two-pane dialogs. */
  fill?: boolean;
}

/**
 * Tri-state per-tool editor.
 *
 *   policy – allow / notify / block.
 *   scope  – owner / everyone.
 *
 * Pairs with `lib/agents/tool-policies.ts` which provides the
 * `'allow' | 'notify' | 'block'` taxonomy and a sensitive-verb
 * heuristic used by the "Set safe defaults" button.
 *
 * The editor is a controlled component — the parent owns the policy
 * map and decides when to persist (typically on Save).
 *
 * A click decides a scope ONLY on the row it was on (S-457, 2026-10-09).
 * The whole map is still written, every listed row (the saves derive the
 * legacy exposure whitelist from it), but a row whose scope nobody chose
 * is written as the bare policy: only a scope click, or "Set safe
 * defaults", writes a scope. Before, any click wrote every row with its
 * SUGGESTED scope, `everyone` for most names, and the owner-only OpenAPI
 * rule reads a saved `everyone` as the owner opening that operation.
 */
export function ToolPolicyEditor({
  available,
  policies,
  onChange,
  label,
  hideBulk,
  fill,
  defaultScope,
}: Props) {
  const t = useT();
  const counts = useMemo(() => {
    const c = { allow: 0, notify: 0, block: 0 };
    for (const tool of available) {
      const raw = policies[tool.name];
      const p = typeof raw === 'string' ? raw : raw?.policy ?? 'allow';
      c[p] += 1;
    }
    return c;
  }, [available, policies]);

  const withPolicy = (name: string, policy: ToolPolicy): ToolPolicyValue => {
    const saved = savedScopeOf(policies[name]);
    return saved ? { policy, scope: saved } : policy;
  };

  /** Every listed row as saved, its scope only where one was saved. */
  const savedMap = (): Record<string, ToolPolicyValue> => {
    const next: Record<string, ToolPolicyValue> = {};
    for (const tool of available) {
      const raw = policies[tool.name];
      next[tool.name] = withPolicy(tool.name, typeof raw === 'string' ? raw : raw?.policy ?? 'allow');
    }
    return next;
  };

  const setOne = (name: string, policy: ToolPolicy) => {
    onChange({ ...savedMap(), [name]: withPolicy(name, policy) });
  };

  const setScope = (name: string, scope: ToolScope) => {
    const current = policies[name];
    const policy = typeof current === 'string' ? current : current?.policy ?? 'allow';
    onChange({ ...savedMap(), [name]: { policy, scope } });
  };

  const setAll = (policy: ToolPolicy) => {
    const next: Record<string, ToolPolicyValue> = {};
    for (const tool of available) next[tool.name] = withPolicy(tool.name, policy);
    onChange(next);
  };

  const setSafeDefaults = () => {
    const next: Record<string, ToolPolicyConfig> = {};
    for (const tool of available) {
      const hints = effectiveHints(tool);
      // SDK decorator hints take precedence over the legacy
      // `destructive` / regex heuristic — `requiresConfirmation` always
      // becomes `notify`, `audience: 'owner'` always becomes the
      // `owner` scope. The destructive flag stays as a tie-breaker for
      // tools without explicit annotations (e.g. MCP/OpenAPI ops where
      // we only know "this is in an actions-group capability").
      const policy = hints.requiresConfirmation
        ? 'notify'
        : tool.destructive
          ? 'notify'
          : suggestSafeDefaultPolicy(tool.name, hints);
      // Never wider than the runtime default: on an owner-only OpenAPI
      // integration "safe" is owner for every operation.
      next[tool.name] = {
        policy,
        scope: defaultScope === 'owner' ? 'owner' : suggestSafeDefaultScope(tool.name, hints),
      };
    }
    onChange(next);
  };

  return (
    <div className={fill ? 'flex min-h-0 flex-1 flex-col space-y-2' : 'space-y-2'}>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold">{label ?? t('agentTools.toolPolicies')}</span>
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">
            {t('agentTools.policyCounts', { allow: counts.allow, notify: counts.notify, block: counts.block })}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={setSafeDefaults}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20"
            title={t('agentTools.safeDefaultsHint')}
          >
            <Sparkles className="h-3 w-3" />
            {t('agentTools.setSafeDefaults')}
          </button>
          {!hideBulk && (
            <>
              <button
                type="button"
                onClick={() => setAll('allow')}
                className="text-[11px] px-2 py-1 rounded-md border border-border hover:bg-muted/30"
              >
                {t('agentTools.allowAll')}
              </button>
              <button
                type="button"
                onClick={() => setAll('notify')}
                className="text-[11px] px-2 py-1 rounded-md border border-border hover:bg-muted/30"
              >
                {t('agentTools.notifyAll')}
              </button>
              <button
                type="button"
                onClick={() => setAll('block')}
                className="text-[11px] px-2 py-1 rounded-md border border-border hover:bg-muted/30"
              >
                {t('agentTools.blockAll')}
              </button>
            </>
          )}
        </div>
      </div>

      {available.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('agentTools.noToolsDiscovered')}</p>
      ) : (
        <div className={`${fill ? 'min-h-0 flex-1' : 'max-h-64'} overflow-y-auto space-y-1 rounded border border-border p-2`}>
          {available.map((tool) => {
            const raw = policies[tool.name];
            const current = typeof raw === 'string' ? raw : raw?.policy ?? 'allow';
            const currentScope = savedScopeOf(raw) ?? defaultScope ?? scopeOf(raw, tool.name);
            const hints = effectiveHints(tool);
            return (
              <div
                key={tool.name}
                className="flex flex-col gap-2 rounded p-1.5 hover:bg-muted/30 sm:flex-row sm:items-start"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium truncate">{tool.name}</span>
                    {tool.destructive && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600">
                        {t('agentTools.modifiesData')}
                      </span>
                    )}
                    {hints.requiresConfirmation && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600"
                        title={t('agentTools.needsConfirmationHint')}
                      >
                        {t('agentTools.needsConfirmation')}
                      </span>
                    )}
                    {hints.audience === 'owner' && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-600"
                        title={t('agentTools.ownerOnlyHint')}
                      >
                        {t('agentTools.ownerOnly')}
                      </span>
                    )}
                    {hints.requiresBridge && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-600"
                        title={t('agentTools.bridgeOnlyHint', { bridge: hints.requiresBridge })}
                      >
                        bridge: {hints.requiresBridge}
                      </span>
                    )}
                  </div>
                  {tool.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {tool.description}
                    </p>
                  )}
                </div>
                <PolicySegmented
                  value={current}
                  onChange={(p) => setOne(tool.name, p)}
                />
                <ScopeSegmented
                  value={currentScope}
                  disabled={current === 'block'}
                  onChange={(scope) => setScope(tool.name, scope)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PolicySegmented({
  value,
  onChange,
}: {
  value: ToolPolicy;
  onChange: (p: ToolPolicy) => void;
}) {
  const opt = (
    p: ToolPolicy,
    label: string,
    title: string,
    activeClass: string,
  ) => {
    const active = value === p;
    return (
      <button
        type="button"
        title={title}
        onClick={() => onChange(p)}
        className={`text-[11px] px-2 py-1 transition-colors ${
          active
            ? activeClass
            : 'text-muted-foreground hover:bg-muted/30'
        }`}
      >
        {label}
      </button>
    );
  };
  return (
    <div className="inline-flex shrink-0 self-start overflow-hidden rounded-md border border-border">
      {opt('allow', 'Allow', 'Run automatically when the agent calls this tool.',
        'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-medium')}
      <span className="w-px bg-border" aria-hidden />
      {opt('notify', 'Notify', 'Ask before each call. The agent pauses until you approve.',
        'bg-amber-500/15 text-amber-700 dark:text-amber-300 font-medium')}
      <span className="w-px bg-border" aria-hidden />
      {opt('block', 'Block', 'Hide this tool from the agent.',
        'bg-rose-500/15 text-rose-700 dark:text-rose-300 font-medium')}
    </div>
  );
}

function ScopeSegmented({
  value,
  disabled,
  onChange,
}: {
  value: ToolScope;
  disabled?: boolean;
  onChange: (scope: ToolScope) => void;
}) {
  const opt = (scope: ToolScope, label: string, title: string) => {
    const active = value === scope;
    return (
      <button
        type="button"
        title={title}
        disabled={disabled}
        onClick={() => onChange(scope)}
        className={`text-[11px] px-2 py-1 transition-colors disabled:cursor-not-allowed ${
          active
            ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300 font-medium'
            : 'text-muted-foreground hover:bg-muted/30'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className={`inline-flex shrink-0 self-start overflow-hidden rounded-md border border-border ${disabled ? 'opacity-50' : ''}`}>
      {opt('everyone', 'Everyone', 'Any authenticated user in this agent context can use this tool.')}
      <span className="w-px bg-border" aria-hidden />
      {opt('owner', 'Owner only', 'Only the agent owner can use this tool.')}
    </div>
  );
}

/**
 * Resolve gating hints for a tool, preferring caller-supplied metadata
 * but falling back to the static decorator-mirror registry. This means
 * legacy callers that don't yet pass `gating` through still get the
 * right safe defaults for known SDK tools.
 */
function effectiveHints(tool: ToolPolicyItem): ToolGatingHints {
  return tool.gating && Object.keys(tool.gating).length > 0
    ? tool.gating
    : gatingHintsFor(tool.name);
}

/** Initialise a policy map for newly discovered tools. */
export function policiesFromAvailable(
  available: ToolPolicyItem[],
  defaultPolicy: ToolPolicy = 'allow',
): Record<string, ToolPolicyConfig> {
  const out: Record<string, ToolPolicyConfig> = {};
  for (const tool of available) {
    const hints = effectiveHints(tool);
    out[tool.name] = {
      policy: defaultPolicy,
      scope: suggestSafeDefaultScope(tool.name, hints),
    };
  }
  return out;
}
